import { useEffect, useState, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import AppointmentCalendar from '@/components/appointment-calendar';
import AppointmentList from '@/components/appointment-list';
import { CalendarDay } from '@/components/appointment-calendar/appointment-calendar.types';
import { AppointmentData } from '@/components/appointment-list/appointment-list.types';
import { useApi } from '@/api/useApi';
import { useQueryProvider } from '@/hooks';
import { CalendarResponse, CalendarSlot } from '@/screens/coach/appointments/appointments.types.ts';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { AppointmentCreateRequestLiveServiceTypeEnum } from '@dopingtech/live_expert_bff_api_ts';
import '@/assets/styles/views/coach/appointments.scss';

const TIMEZONE = 'Europe/Istanbul';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault(TIMEZONE);

const AppointmentsPage = () => {
	const { CalendarControllerApi, AppointmentControllerApi } = useApi();

	const [selectedDate, setSelectedDate] = useState(dayjs().tz(TIMEZONE));
	const [allAppointments, setAllAppointments] = useState<{
		active: AppointmentData[];
		cancelled: AppointmentData[];
	}>({ active: [], cancelled: [] });
	const [calendarData, setCalendarData] = useState<CalendarSlot[]>([]);
	const [activeTab, setActiveTab] = useState<'active' | 'cancelled'>('active');
	const [counts, setCounts] = useState({ active: 0, cancelled: 0 });
	const [cancellationCount, setCancellationCount] = useState(0);
	const [selectedDay, setSelectedDay] = useState<CalendarDay>({
		date: selectedDate.toDate(),
		isCurrentMonth: true,
		dayName: selectedDate.format('ddd'),
		stats: {
			value: '0',
			isZero: true
		},
		hasAppointment: false,
		hasSlot: true
	});

	useQueryProvider(
		[QUERY_KEYS.countExpertCalendarSlotAppointmentBetweenDates, selectedDate.format('YYYY-MM'), AppointmentCreateRequestLiveServiceTypeEnum.OneOnOneCoaching],
		() => CalendarControllerApi.countExpertCalendarSlotAppointmentBetweenDates(selectedDate.year(), selectedDate.month() + 1, AppointmentCreateRequestLiveServiceTypeEnum.OneOnOneCoaching),
		{
			onSuccess: (response: CalendarResponse) => {
				if (response?.data) {
					setCalendarData(response?.data?.data);
				} else {
					setCalendarData([]);
				}
			}
		}
	);

	useQueryProvider(
		[QUERY_KEYS.getAppointmentCancellationCount, selectedDate.format('YYYY-MM'), AppointmentCreateRequestLiveServiceTypeEnum.OneOnOneCoaching],
		() => AppointmentControllerApi.getAppointmentCancellationCount(selectedDate.year(), selectedDate.month() + 1, AppointmentCreateRequestLiveServiceTypeEnum.OneOnOneCoaching),
		{
			onSuccess: response => {
				if (response?.data?.data !== undefined) {
					setCancellationCount(response.data.data);
				}
			}
		}
	);

	const { isLoading: isLoadingActive } = useQueryProvider(
		[QUERY_KEYS.findAppointmentsOfTheDay, selectedDate.tz(TIMEZONE).format('YYYY-MM-DD'), 'CREATED', AppointmentCreateRequestLiveServiceTypeEnum.OneOnOneCoaching],
		() =>
			AppointmentControllerApi.findAppointmentsOfTheDay(selectedDate.tz(TIMEZONE).format('YYYY-MM-DD'), 'CREATED', AppointmentCreateRequestLiveServiceTypeEnum.OneOnOneCoaching, undefined, 200, ['appointmentStartDate,ASC']),
		{
			enabled: selectedDay?.stats?.isZero === false,
			refetchOnWindowFocus: true,
			refetchOnMount: 'always',
			onSuccess: response => {
				if (response) {
					setAllAppointments(prev => ({
						...prev,
						active: response?.data?.data || []
					}));
					setCounts(prev => ({
						...prev,
						active: response.data.data.totalElements || 0
					}));
				}
			}
		}
	);

	const { isLoading: isLoadingCancelled } = useQueryProvider(
		[QUERY_KEYS.findAppointmentsOfTheDay, selectedDate.tz(TIMEZONE).format('YYYY-MM-DD'), 'CANCELLED', AppointmentCreateRequestLiveServiceTypeEnum.OneOnOneCoaching],
		() =>
			AppointmentControllerApi.findAppointmentsOfTheDay(
				selectedDate.tz(TIMEZONE).format('YYYY-MM-DD'),
				'CANCELLED',
				AppointmentCreateRequestLiveServiceTypeEnum.OneOnOneCoaching,
				undefined,
				200,
				['appointmentStartDate,ASC']
			),
		{
			refetchOnWindowFocus: true,
			refetchOnMount: 'always',
			onSuccess: response => {
				if (response) {
					setAllAppointments(prev => ({
						...prev,
						cancelled: response?.data?.data || []
					}));
					setCounts(prev => ({
						...prev,
						cancelled: response.data.data.totalElements || 0
					}));
				}
			}
		}
	);

	const { currentAppointments } = useMemo(() => {
		return {
			currentAppointments: allAppointments[activeTab]
		};
	}, [allAppointments, activeTab]);

	useEffect(() => {
		const safeCalendarData = Array.isArray(calendarData) ? calendarData : [];
		const formattedDateStr = selectedDate.tz(TIMEZONE).format('YYYY-MM-DD');

		const selectedDayData = safeCalendarData.find(slot => slot.slotDate === formattedDateStr);

		if (selectedDayData) {
			setSelectedDay(prev => ({
				...prev,
				date: selectedDate.toDate(),
				dayName: selectedDate.format('ddd'),
				isCurrentMonth: selectedDate.isSame(dayjs().tz(TIMEZONE), 'month'),
				stats: {
					value: `${selectedDayData.appointmentCount}/${selectedDayData.slotCount}`,
					isZero: selectedDayData.appointmentCount === 0
				},
				hasSlot: selectedDayData.slotCount > 0,
				hasAppointment: selectedDayData.appointmentCount > 0
			}));
		}
	}, [selectedDate, calendarData]);

	const handleMonthChange = useCallback((date: Date) => {
		const newDate = dayjs(date).tz(TIMEZONE);
		setSelectedDate(prev => prev.month(newDate.month()).year(newDate.year()));
	}, []);

	const handleTabChange = useCallback((tab: 'active' | 'cancelled') => {
		setActiveTab(tab);
	}, []);

	const handleDateSelect = useCallback((date: Date) => {
		const newSelectedDate = dayjs(date).tz(TIMEZONE);
		setSelectedDate(newSelectedDate);
		setActiveTab('active');
	}, []);

	return (
		<div className="appointments-page">
			<AppointmentCalendar
				onDayClick={handleDateSelect}
				selectedDate={selectedDate.toDate()}
				calendarData={calendarData}
				onMonthChange={handleMonthChange}
				cancellationCount={cancellationCount}
			/>
			<AppointmentList
				appointments={currentAppointments}
				selectedDay={selectedDay}
				isLoading={isLoadingActive || isLoadingCancelled}
				activeTab={activeTab}
				onTabChange={handleTabChange}
				counts={counts}
				cancellationCount={cancellationCount}
			/>
		</div>
	);
};

export default AppointmentsPage;
